/**
 * What members have sent through the Submission Portal.
 *
 * ⚠️ This is NOT COMM-PASS's queue and does not touch it. The portal keeps its
 * own table precisely so a member's work never appears in the live queue
 * uninvited — which left AAS with nowhere to see the finished ones. This is
 * that place.
 *
 * Sorted newest first and showing the grade the member was shown when they
 * sent. A specialist opening this wants to know two things before clicking:
 * how complete it is, and whether the paperwork is here.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LOB_NAME, type SubmissionGrade } from '@commpass/shared';
import { BackIcon } from '../components/Icons';
import { ApiError, api } from '../lib/api';

interface QueueRow {
  id: string;
  businessName: string | null;
  className: string | null;
  agentEmail: string;
  sentAt: string | null;
  hasQuestionnaire: boolean;
  lines: string[];
  documentCount: number;
  grade: SubmissionGrade;
  gradeNow: SubmissionGrade;
}

export function PortalQueue() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<QueueRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .portalQueue()
      .then((r) => setRows(r.submissions))
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'Could not load the queue.'));
  }, []);

  return (
    <div className="wrap">
      <div className="back-link" onClick={() => navigate('/hub')}>
        <BackIcon width={16} height={16} /> Back to the hub
      </div>
      <div className="page-head">
        <div>
          <div className="page-title">Member submissions</div>
          <div className="page-sub">
            Built and sent by members through the Submission Portal. Separate from the COMM-PASS
            queue — nothing here has been through the specialist intake.
          </div>
        </div>
      </div>

      {error && <div className="empty">{error}</div>}
      {rows && !rows.length && !error && (
        <div className="empty">
          <p>Nothing sent yet.</p>
          <p className="sub-meta">Members' drafts stay out of here until they actually send them.</p>
        </div>
      )}

      {rows?.map((r) => (
        <div key={r.id} className="panel ps-row" onClick={() => navigate(`/submission/${r.id}`)} role="button" tabIndex={0}>
          <div className={`ps-grade is-${r.grade.band}`}>{r.grade.grade}</div>
          <div className="ps-main">
            <div className="ps-name">{r.businessName || 'Untitled'}</div>
            <div className="sub-meta">
              {[r.className, r.lines.map((l) => LOB_NAME[l as keyof typeof LOB_NAME] ?? l).join(', '), r.agentEmail]
                .filter(Boolean)
                .join(' · ')}
            </div>
          </div>
          <div className="pq-facts sub-meta">
            {/* Document count leads: it is the first thing a specialist wants to
                know, and the difference between work they can start now and a
                chase. */}
            <div className={r.documentCount ? 'pq-docs' : 'pq-docs is-none'}>
              {r.documentCount ? `${r.documentCount} document${r.documentCount === 1 ? '' : 's'}` : 'No documents'}
            </div>
            <div>
              {r.hasQuestionnaire ? 'Questionnaire attached · ' : ''}
              {r.sentAt ? new Date(r.sentAt).toLocaleDateString('en-US', { dateStyle: 'medium' }) : ''}
            </div>
            {/* Only shown when it differs. A grade that changed after sending
                means someone edited the submission afterwards, which is worth
                noticing and is silent otherwise. */}
            {r.gradeNow.score !== r.grade.score && (
              <div className="pq-drift">Now grades {r.gradeNow.grade}</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
